<script>
import TinyColor from 'tinycolor2';
const SvgComponent = () => import('@/Scripts/components/shared/svg/Svg.vue');
const Chevron = () => import('@/Scripts/components/shared/svg/Chevron/Chevron.vue');
const Arrow = () => import('@/Scripts/components/shared/svg/Arrow/Arrow.vue');

export default {
  components: {
    SvgComponent,
    Chevron,
    Arrow
  },
  props: {
    buttonV3: {
      type: Boolean,
      default: true
    },
    id: {
      type: String
    },
    /**
     * Default Text, overides slot text(will be removed), allows for updating button text.
     */
    text: {
      type: String
    },
    /**
     * Binds to the HTML href property. This property is also the trigger for using the button as a link(<a>).
     */
    href: {
      type: String
    },
    /**
     * Sets target property of the link (<a>).
     */
    target: {
      type: String
    },
    /**
    * Defines the colortheme for the button, valid properties are: 'cta' - Call to action green, 'main' - white on black, 'inverted' - black on white with border.
    */
    theme: {
      type: String,
      default: 'main',
      validator: value => ['cta', 'main', 'inverted', 'arrow', 'link'].includes(value)
    },
    /**
    * Removes the background, and adds the theme color to border and text.
    */
    ghost: {
      type: Boolean,
      default: false
    },
    /**
     * Overrides the text color
     */
    overrideThemeColor: {
      type: String
    },
    /**
     * Force text color on button
     */
    forceTextColor: {
      type: String
    },
    /**
     * Makes the button fill available space in parent.
     */
    block: {
      type: Boolean,
      default: false
    },
    /**
     * Deactivates the button, greys out on hover.
     */
    disabled: {
      type: Boolean,
      default: false
    },
    /**
     * Alternaltive text to be show when button is disabled.
     */
    disabledText: {
      type: String
    },
    /**
     * Makes the button shake on hover when button is disabled.
     */
    disabledShake: {
      type: Boolean,
      default: false
    },
    /**
     * Makes the button shake on hover when button is disabled.
     */
    notDisabledUntilHover: {
      type: Boolean,
      default: false
    },
    /**
     * Deactivates, grays out, and displays animated loading icon on button.
     */
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * Alternaltive text to be show when button is set to loading.
     */
    loadingText: {
      type: String
    },
    /**
     * Object for icon to be displayed next to text. Keys: 'name' & 'size'.
     */
    icon: {
      type: Object
    },
    /**
     * Sets the position of the icon, valid properties are 'before' and 'after'. This property is also the trigger for showing the icon, a falsy value hides the icon.
     */
    iconPlacement: {
      type: String,
      validator: value => ['before', 'after', ''].includes(value)
    },
    chevronDirection: {
      type: String,
      validator: value => ['left', 'right', 'down', 'up', ''].includes(value)
    },
    /**
     * Shows the small version of the button.
     */
    small: {
      type: Boolean,
      default: false
    },
    smallest: Boolean,
    /**
     * Binds to the HTML form property, not supported with links.
     */
    form: {
      type: String,
      default: null
    },
    /**
     * Binds to the HTML tabindex property.
     */
    tabindex: {
      type: String,
      default: '0'
    },
    arrowBack: Boolean,
    shadow: Boolean,
    pill: Boolean,
    ariaLabel: String
  },
  data () {
    return {
      slotWidth: 'unset',
      hover: false,
      textTooLong: false,
      hasBeenHovered: false,
      ready: false
    };
  },
  watch: {
    hover (newVal) {
      if (newVal) {
        this.hasBeenHovered = newVal;
      }
    }
  },
  computed: {
    tagType () {
      return this.href ? 'a' : 'button';
    },
    classList () {
      return {
        '-cta': this.theme === 'cta',
        '-main': this.theme === 'main',
        '-inverted': this.theme === 'inverted',
        '-ghost': this.ghost,
        '-block': this.block,
        '-disabled': this.disabled,
        '-shake': this.disabledShake,
        '-loading': this.loading,
        'font-body-md': !this.small,
        '-small font-responsive-small': this.small && !this.smallest,
        '-small -smallest font-responsive-smallest': this.smallest,
        '-icon-before': this.iconPlacement === 'before'
      };
    },
    contrastingColor () {
      if (this.forceTextColor) {
        return this.forceTextColor;
      }
      if (this.overrideThemeColor) {
        return TinyColor.mostReadable(this.overrideThemeColor, ['#FCFCFC', '#1D1D1D']).toHexString();
      } else {
        return '';
      }
    },
    modifiedColor () {
      if (this.overrideThemeColor) {
        const color = TinyColor(this.overrideThemeColor);
        if (color.isDark()) {
          return color.lighten(7).toHexString();
        } else {
          return color.darken(10).toHexString();
        }
      } else {
        return '';
      }
    },
    styleOverride () {
      const obj = {};
      if (this.isDisabled && this.hover) return obj;
      if (this.overrideThemeColor && this.ghost) {
        obj.color = `${this.overrideThemeColor} !important`;
        obj.borderColor = `${this.overrideThemeColor} !important`;
        if (this.hover) {
          obj.backgroundColor = `${this.overrideThemeColor} !important`;
          obj.color = `${this.contrastingColor} !important`;
        }
      } else if (this.overrideThemeColor) {
        obj.backgroundColor = `${this.overrideThemeColor} !important`;
        obj.color = `${this.contrastingColor} !important`;
        obj.borderColor = 'transparent !important';
        if (this.hover) {
          obj.backgroundColor = `${this.modifiedColor} !important`;
        }
      }
      return obj;
    },
    loadingIcon () {
      return {
        name: 'Loading',
        color: this.overrideThemeColor || this.Globals.colors.grayDark,
        size: this.small ? 12 : 16
      };
    },
    slotText () {
      if (this.$slots && this.$slots.default) {
        return this.$slots.default[0].text;
      }
      return ' ';
    },
    loadingDisplay () {
      return this.loadingText || this.text || this.slotText;
    },
    disabledDisplay () {
      return this.disabledText || this.text || this.slotText;
    },
    regularDisplay () {
      return this.text || this.slotText;
    },
    titleDisplay () {
      return (this.loading && this.loadingDisplay) || (this.disabled && this.disabledDisplay) || this.regularDisplay;
    },
    isDisabled () {
      if (!this.notDisabledUntilHover) return this.disabled;
      return this.hasBeenHovered && this.disabled;
    }
  },
  methods: {
    onClick (e) {
      if ((!this.href || this.disabled || this.loading) && !this.form) e.preventDefault();
      if (!this.disabled && !this.loading) this.$emit('click', e);
    }
  },
  mounted () {
    const observer = new IntersectionObserver(e => {
      if (e && e[0].isIntersecting) {
        this.$nextTick(() => {
          this.ready = true;
        });
      }
    });
    if (this.$el) {
      observer.observe(this.$el);
    } else {
      this.$nextTick(() => {
        this.ready = true;
      });
    }
  }
};
</script>

<template>
  <div class="btn-v2" :class="{'-ready': ready, '-shadow': shadow, '-pill': pill}">
   <div
      v-if="theme === 'arrow' || theme === 'link'"
     class="d-flex align-items-center arrow-link"
   >
      <Arrow
        v-if="arrowBack"
        class="m-r-3"
        :thickness="'light'"
        :direction="'left'"
        :color="this.overrideThemeColor"
      />
      <component
        :is="tagType"
        @click="onClick($event)"
        class="p-0"
        :class="classList"
        :href="href"
        :role="!href ? 'button' : ''"
        :tabindex="`${tabindex}`"
        :style="{ color: overrideThemeColor }"
      >
        {{ regularDisplay }}
      </component>
      <Arrow
        v-if="!arrowBack && !theme === 'link'"
        class="m-l-3"
        :thickness="'light'"
        :direction="'right'"
        :color="this.overrideThemeColor"
      />
    </div>
    <button
      v-else-if="!href"
      :id="id"
      :class="[classList, { '-text-too-long': textTooLong }]"
      @click="onClick($event)"
      @mouseover="hover = true"
      @mouseleave="hover = false"
      :aria-label="ariaLabel"
      :aria-disabled="isDisabled"
      :aria-busy="loading"
      :tabindex="tabindex"
      :form="form"
      :style="styleOverride"
      :title="textTooLong && titleDisplay"
    >
    <span class="slots" ref="slots">
      <span
        class="no-font-spacing"
        v-if="loading"
        key="loadingText"
        v-html="loadingDisplay"
      />
      <span
        class="no-font-spacing"
        v-else-if="isDisabled"
        key="disabledText"
        v-html="disabledDisplay"
      />
      <span
        class="no-font-spacing"
        v-else
        key="defaultText"
        v-html="regularDisplay"
      />

      <transition name="icon">
        <SvgComponent
          class="loadingSvg"
          v-if="loading"
          :icon="loadingIcon"
          key="loading"
        />
        <SvgComponent
          class="iconSvg"
          v-else-if="iconPlacement"
          :icon="icon"
          key="icon"
        />
        <Chevron
          v-else-if="chevronDirection"
          :direction="chevronDirection"
          :thickness="'thin'"
          :size="12"
          key="chevron"
        />
      </transition>
      </span>
    </button>
    <a
      v-else-if="href && theme !== 'arrow'"
      :id="id"
      class="-add-focus-box"
      :class="[classList, { '-text-too-long': textTooLong }]"
      @click="onClick($event)"
      @keydown.space="onClick($event)"
      @keydown.enter="onClick($event)"
      @mouseover="hover = true"
      @mouseleave="hover = false"
      :aria-disabled="isDisabled"
      :aria-busy="loading"
      :href="href"
      role="button"
      :target="target"
      :tabindex="`${tabindex}`"
      :style="styleOverride"
      :title="textTooLong && titleDisplay"
    >
    <span class="slots" ref="slots">
      <span
        class="no-font-spacing"
        v-if="loading"
        key="loadingText"
        v-html="loadingDisplay"
      />
      <span
        class="no-font-spacing"
        v-else-if="isDisabled"
        key="disabledText"
        v-html="disabledDisplay"
      />
      <span
        class="no-font-spacing"
        v-else
        key="defaultText"
        v-html="regularDisplay"
      />
      </span>
      <transition name="icon">
        <SvgComponent
          class="loadingSvg"
          v-if="loading"
          :icon="loadingIcon"
          key="loading"
        />
        <SvgComponent
          class="iconSvg"
          v-else-if="iconPlacement"
          :icon="icon"
          key="icon"
        />
        <Chevron
          v-else-if="chevronDirection"
          :direction="chevronDirection"
          :thickness="'thin'"
          :size="12"
          key="chevron"
        />
      </transition>
    </a>
  </div>
</template>

<style lang="scss">
@import "@/scss/defaults.scss";
 // Focus disabeling and reenableing when tabbing.

 .user-is-tabbing .btn-v2 button:focus,
 .user-is-tabbing .btn-v2 a:focus {
  outline: 5px auto -webkit-focus-ring-color;
}
.btn-v2.-ready {
  opacity: 1 !important;
  button,
  a {
    .slots {
      transition: width 0.35s;
    }
  }
}
.btn-v2 {
  &.-shadow {
    button, a {
      box-shadow: 1px 1px 5px 1px #00000024;
    }
  }
  &.-pill {
    button, a {
      border-radius: 18px !important;
    }
  }
}
.btn-v2.btn-v2.btn-v2 {
  display: flex;
  opacity: 0;
  .arrow-link {
    button {
      background-color: transparent;
    }
    button, a {
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }
  button {
    color: $black;
  }
  button,
  a {
    border-width: 1px;
    border-style: solid;
    border-radius: 6px;
    padding: 0 rem(18px);
    border-color: transparent;
    transition: all 0.35s;
    flex-grow: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    max-width: 100%;
    max-height: rem(44px);
    text-decoration: none;

    @include media-breakpoint-up(lg) {
      padding-left: 24px;
      padding-right: 24px;
    }

    // Small button adjustments and sizing

    &.-small {
      padding: 0 rem(12px);
      max-height: rem(30px);

      .slots {
        height: rem(28.7px);

        span {
          top: rem(10px);
        }
      }

      svg {
        height: 12px;
        width: 12px;
      }
    }
    &.-smallest {
      padding: 0 rem(9px);
      max-height: rem(24px);
      .slots {
        height: rem(23.4px);

        span {
          top: rem(5px);
        }
      }
    }
    &.-block {
      flex-grow: 1;
    }

    // Loading and disabled states

    &.-disabled:hover {
      background-color: $gray !important;
      border-color: transparent !important;
      color: $gray-dark !important;
      cursor: default;

      .svgfill {
        fill: $gray-dark !important;
      }
    }

    &.-shake.-disabled:hover {
      animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
    }

    // Themes
    &.-ghost {
      background: none;
    }

    // Call to action Theme

    &.-cta {
      background: $blue;
      color: $white;

      &.-ghost {
        border-color: $blue;
        background: none;
        color: $blue;

        &:hover {
          background: $blue;
          color: $white;
        }
      }

      &:hover {
        background: darken($blue, 7.5%);
      }
    }

    // Main Theme (Default)
    &.-main {
      color: $white;
      background: $black;

      &.-ghost {
        border-color: $black;
        background: none;
        color: $black;

        &:hover {
          color: $white;
          background: $black;
        }
      }

      &:hover {
        background: lighten($black, 15%);
      }
    }

    // Inverted Theme

    &.-inverted {
      border-color: $black;
      background-color: $white;
      color: $black;

      &.-ghost {
        border-color: $white;
        background: none;
        color: $white;

        &:hover {
          color: $black;
          background: $white;
        }
      }

      &:hover {
        background: darken($white, 15%);
      }
    }

    // Button text transitions and placement
    &.-text-too-long .slots span {
      text-overflow: ellipsis;
    }

    .hidden-width-setters {
      opacity: 0;
      position: absolute;
      white-space: nowrap;
    }

    .slots {
      height: rem(42px);
      // position: relative;
      overflow: hidden;
      flex-grow: 0;
      display: flex;
      align-items: center;
      //transition: width 0.35s;

      .hidden-width-keeper {
        opacity: 0;
      }

      > span {
        transition: opacity 0.35s;
        white-space: nowrap;
        max-width: 101%;
        // position: absolute;
        // top: rem(15px);
        // left: 0;
        min-width: 0;

        &.text-enter,
        &.text-leave-to {
          opacity: 0;
          margin: 0;
        }
        &.text-enter-to,
        &.text-leave {
          opacity: 1;
        }
      }
    }

    // Icons transitions and styling

    &.-icon-before .icon-enter,
    &.-icon-before .icon-leave-to,
    &.-small .icon-enter,
    &.-small .icon-leave-to,
    .icon-enter,
    .icon-leave-to {
      fill-opacity: 0;
      width: 0;
      margin: 0;
    }

    &.-icon-before {
      flex-direction: row-reverse;

      svg {
        margin-right: rem(6px);
        margin-left: 0;
      }
    }

    &.-ghost svg .svgfill {
      transition: fill 0.5s;
    }

    svg {
      margin-left: rem(6px);
      flex-shrink: 0;
      /* transition: all 0.5s; */
      .svgfill {
        fill: currentColor;
      }
      polygon[fill="#1D1D1D"] {
        fill: currentColor;
      }
    }
  }

  // Shake animation.

  @keyframes shake {
    10%,
    90% {
      transform: translate3d(-1px, 0, 0);
    }

    20%,
    80% {
      transform: translate3d(2px, 0, 0);
    }

    30%,
    50%,
    70% {
      transform: translate3d(-4px, 0, 0);
    }

    40%,
    60% {
      transform: translate3d(4px, 0, 0);
    }
  }
}
</style>
